import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaEdit,
  FaCalendarAlt,
  FaHeading,
  FaTags,
  FaAlignLeft,
  FaSpinner,
} from "react-icons/fa";
import {
  getUpdateById,
  updateUpdateById,
} from "../../../../services/updatesServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const initialForm = {
  date_update: "",
  title_update: "",
  content_update: "",
  tag_update: "",
  id: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

function normalizeDateInput(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const EditUpdateModal = ({ isOpen, onClose, updateId, refreshList }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchUpdate = async () => {
      if (!updateId || !isOpen) return;

      try {
        setLoading(true);

        const update = await getUpdateById(updateId);

        setFormData({
          date_update: normalizeDateInput(update.date_update),
          title_update: update.title_update || "",
          content_update: update.content_update || "",
          tag_update: update.tag_update || "",
          id: update.id || "",
        });
      } catch (error) {
        console.error("Erreur lors de la récupération de la mise à jour :", error);
        toast.error("Erreur lors du chargement de la mise à jour");
      } finally {
        setLoading(false);
      }
    };

    fetchUpdate();
  }, [updateId, isOpen]);

  const datePreview = useMemo(() => {
    return formatDate(formData.date_update);
  }, [formData.date_update]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content_update: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!formData.id) {
      toast.error("Mise à jour introuvable.");
      return;
    }

    if (!formData.date_update || !formData.title_update.trim()) {
      toast.error("Veuillez renseigner au moins la date et le titre.");
      return;
    }

    try {
      setSaving(true);
      await updateUpdateById(formData.id, formData);
      refreshList?.();
      onClose?.();
      toast.success("Mise à jour modifiée avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour", error);
      toast.error("Erreur lors de la modification de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />

            <div className="relative px-5 py-5 text-white sm:px-6 sm:py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaEdit />
                    Modification
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    Modifier la mise à jour
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Mets à jour le titre, le contenu, la date et le tag de cette
                    annonce.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            {loading ? (
              <div className="flex min-h-[420px] items-center justify-center p-8">
                <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                  <FaSpinner className="animate-spin" />
                  Chargement de la mise à jour...
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                  <div className="space-y-6 xl:col-span-8">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Informations principales"
                        subtitle="Date, titre et tag de classification."
                      />

                      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field>
                          <Label required>Date</Label>
                          <div className="relative">
                            <FaCalendarAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="date"
                              name="date_update"
                              value={formData.date_update}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>

                        <Field>
                          <Label>Tag</Label>
                          <div className="relative">
                            <FaTags className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="tag_update"
                              value={formData.tag_update}
                              onChange={handleChange}
                              placeholder="Nouveauté, Correctif, Sécurité..."
                            />
                          </div>
                        </Field>

                        <Field className="md:col-span-2">
                          <Label required>Titre</Label>
                          <div className="relative">
                            <FaHeading className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              type="text"
                              name="title_update"
                              value={formData.title_update}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Field>
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Contenu"
                        subtitle="Détaille la mise à jour avec une mise en forme simple."
                      />

                      <div className="mt-5">
                        <Label>Contenu</Label>

                        <div className="mt-2 overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-slate-700">
                          <ReactQuill
                            value={formData.content_update}
                            onChange={handleContentChange}
                            modules={quillModules}
                            formats={quillFormats}
                            className="update-quill"
                            theme="snow"
                          />
                        </div>
                      </div>
                    </section>
                  </div>

                  <div className="space-y-6 xl:col-span-4">
                    <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <SectionTitle
                        title="Résumé"
                        subtitle="Aperçu rapide avant enregistrement."
                      />

                      <div className="mt-5 space-y-4">
                        <SummaryRow
                          icon={<FaCalendarAlt />}
                          label="Date"
                          value={datePreview}
                        />
                        <SummaryRow
                          icon={<FaHeading />}
                          label="Titre"
                          value={formData.title_update || "Non renseigné"}
                        />
                        <SummaryRow
                          icon={<FaTags />}
                          label="Tag"
                          value={formData.tag_update || "Aucun tag"}
                        />
                        <SummaryRow
                          icon={<FaAlignLeft />}
                          label="Contenu"
                          value={
                            formData.content_update
                              ? "Contenu renseigné"
                              : "Aucun contenu"
                          }
                        />
                      </div>
                    </section>

                    <section className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                      <div className="text-sm font-semibold">
                        Modification sécurisée
                      </div>

                      <p className="mt-2 text-sm leading-6 opacity-80">
                        Les changements seront visibles après sauvegarde et
                        rafraîchissement de la liste.
                      </p>
                    </section>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Sauvegarder"}
              </button>
            </div>
          </div>

          <QuillStyles />
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>

      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>

      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>

        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function QuillStyles() {
  return (
    <style>{`
      .update-quill .ql-toolbar.ql-snow {
        border: 0;
        border-bottom: 1px solid rgb(226 232 240);
        padding: 12px;
        background: linear-gradient(to right, rgba(248,250,252,0.9), rgba(241,245,249,0.9));
      }

      .update-quill .ql-container.ql-snow {
        border: 0;
        min-height: 190px;
        font-size: 0.95rem;
      }

      .update-quill .ql-editor {
        min-height: 190px;
        color: #0f172a;
      }

      .dark .update-quill .ql-toolbar.ql-snow {
        background: rgba(15, 23, 42, 0.92);
        border-bottom: 1px solid rgb(51 65 85);
      }

      .dark .update-quill .ql-container.ql-snow {
        background: rgb(15 23 42);
      }

      .dark .update-quill .ql-editor {
        color: #e2e8f0;
      }

      .dark .update-quill .ql-stroke {
        stroke: #cbd5e1;
      }

      .dark .update-quill .ql-fill {
        fill: #cbd5e1;
      }

      .dark .update-quill .ql-picker {
        color: #cbd5e1;
      }
    `}</style>
  );
}

export default EditUpdateModal;